/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { get } from 'embercom/lib/ajax';
import type { FinSetupBehaviorSaveParams, HitlFrequency } from 'embercom/lib/operator/fin/types';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';

const FIN_EMAIL_HITL_FF = 'fin-email-hitl';
const FIN_EMAIL_HITL_VOLUME_FF = 'fin-email-hitl-volume-helper';
const DEFAULT_FREQUENCY: HitlFrequency = 'rarely';

// Mirrors FinSetupProfilesController::FREQUENCY_TO_CONFIDENCE_THRESHOLD on the backend.
// Used here to project the workspace's average daily email volume onto a per-frequency
// "routes around N conversations a day" hint — the displayed number changes live as the
// user clicks between frequencies, no extra request needed.
const FREQUENCY_THRESHOLDS: Record<HitlFrequency, number> = {
  rarely: 0.3,
  sometimes: 0.5,
  often: 0.7,
};

const FREQUENCY_TRANSLATION_KEYS: Record<HitlFrequency, string> = {
  rarely: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.rarely',
  sometimes: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.sometimes',
  often: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.often',
};

const FREQUENCY_HELPER_KEYS: Record<HitlFrequency, string> = {
  rarely: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-rarely',
  sometimes: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-sometimes',
  often: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-often',
};

const FREQUENCY_VOLUME_HELPER_KEYS: Record<HitlFrequency, string> = {
  rarely: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.volume-helper-rarely',
  sometimes:
    'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.volume-helper-sometimes',
  often: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.volume-helper-often',
};

type Option = 'on' | 'off';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (
    params: FinSetupBehaviorSaveParams,
    options?: {
      messages?: { success?: string; failure?: string };
      rethrow?: boolean;
    },
  ) => Promise<void> | void;
}

interface Signature {
  Args: Args;
}

export default class AskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  @tracked selectedFrequency: HitlFrequency =
    this.behavior.hitlSettings?.frequency ?? DEFAULT_FREQUENCY;
  @tracked isSaving = false;
  @tracked avgDailyEmailConversations: number | null = null;

  // Tracks the last successfully persisted state so we can detect unsaved
  // changes and revert on close without saving.
  private savedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  private savedFrequency: HitlFrequency =
    this.behavior.hitlSettings?.frequency ?? DEFAULT_FREQUENCY;

  get frequencies(): { value: HitlFrequency; translationKey: string }[] {
    return (Object.keys(FREQUENCY_TRANSLATION_KEYS) as HitlFrequency[]).map((value) => ({
      value,
      translationKey: FREQUENCY_TRANSLATION_KEYS[value],
    }));
  }

  get showVolumeHelper(): boolean {
    // Hide the volume sentence when the rounded per-frequency estimate is 0 — either
    // a brand-new workspace, an ES failure fallback, or a volume so low that the
    // threshold multiplication rounds to zero. "Routes around 0 conversations a day"
    // reads strangely in all cases, so we fall back to the complexity-only copy.
    return this.canUseFinEmailHitlVolumeHelper && this.estimatedConversationsPerDay > 0;
  }

  get frequencyHelperKey(): string {
    return this.showVolumeHelper
      ? FREQUENCY_VOLUME_HELPER_KEYS[this.selectedFrequency]
      : FREQUENCY_HELPER_KEYS[this.selectedFrequency];
  }

  get estimatedConversationsPerDay(): number {
    let avg = this.avgDailyEmailConversations ?? 0;
    return Math.round(avg * FREQUENCY_THRESHOLDS[this.selectedFrequency]);
  }

  get behavior(): Profile {
    return this.args.ruleset.rulesetLinks.firstObject.object;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_FF));
  }

  get canUseFinEmailHitlVolumeHelper(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_VOLUME_FF));
  }

  get options() {
    return [
      {
        value: 'off' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.description',
      },
      {
        value: 'on' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.description',
      },
    ];
  }

  get isEnabled(): boolean {
    return this.selectedOption === 'on';
  }

  get statusLabel(): string {
    return this.intl.t(
      this.isEnabled
        ? 'operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status'
        : 'operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status',
    );
  }

  get isInteractionDisabled(): boolean {
    return this.isSaving;
  }

  @action
  setSelectedOption(option: Option) {
    if (this.isInteractionDisabled || option === this.selectedOption) {
      return;
    }
    this.selectedOption = option;
  }

  @action
  setSelectedFrequency(frequency: HitlFrequency) {
    if (this.isInteractionDisabled || frequency === this.selectedFrequency) {
      return;
    }
    this.selectedFrequency = frequency;
  }

  @action
  async save(closeAccordion: () => void) {
    if (this.isInteractionDisabled) {
      return;
    }

    this.isSaving = true;

    let hitlSettings: { enabled: boolean; frequency?: HitlFrequency } =
      this.selectedOption === 'on'
        ? { enabled: true, frequency: this.selectedFrequency }
        : { enabled: false };

    try {
      await this.args.partialSave(
        { hitl_settings: hitlSettings },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
      this.savedOption = this.selectedOption;
      this.savedFrequency = this.selectedFrequency;
      closeAccordion();
    } catch {
      this.selectedOption = this.savedOption;
      this.selectedFrequency = this.savedFrequency;
    } finally {
      this.isSaving = false;
    }
  }

  @action
  revertPendingChanges() {
    if (this.isSaving) {
      // Let the in-flight save complete — it will record the correct savedOption/
      // savedFrequency and call closeAccordion() itself.
      return;
    }
    this.selectedOption = this.savedOption;
    this.selectedFrequency = this.savedFrequency;
  }

  @action
  close(closeAccordion: () => void) {
    this.revertPendingChanges();
    closeAccordion();
  }

  @action
  async loadAvgVolume() {
    if (!this.canUseFinEmailHitlVolumeHelper) {
      return;
    }

    let appId = this.appService?.app?.id;
    if (!appId) {
      return;
    }

    try {
      let response: { conversations_per_day: number } = await get(
        `/ember/ai_agent/workspace_volume/email_conversations_per_day?app_id=${appId}`,
      );
      let count = response?.conversations_per_day;
      this.avgDailyEmailConversations = Number.isFinite(count) ? count : null;
    } catch {
      // Leave avgDailyEmailConversations null so the helper falls back to the
      // complexity-only copy. No user-facing toast: this is a non-essential decoration.
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate': typeof AskATeammate;
    'operator/fin/setup/sections/ask-a-teammate': typeof AskATeammate;
  }
}
